/**
 * The one decision model (ADR 0072): every surface that answers a request (the Inbox, the session
 * drawer, Overview's Needs you, mission pages, the bell and the phone) offers the same actions
 * with the same names, applies the same high-risk rule, and lists what waits in the same groups
 * and the same order. Pure, so tested; the components read it.
 *
 * - Actions: Allow once, Allow for session (the pattern visible and editable), Make it a rule
 *   (scope 'project', one request writes one rule), Deny (an optional note).
 * - High risk: allowing it always takes a confirm that names it. Never a key, a swipe or a
 *   one-click button, alone or in bulk. Denying needs no confirm.
 * - Groups: Requests, Questions (held questions, plans and forms), Blocked, Assistant, Sessions
 *   waiting, Asked. Order inside a group: the soonest deadline, then the highest risk, then the
 *   longest wait.
 * - Undo: the hub cannot take an answer back (the hook has returned and the call may have run),
 *   so an answer has no Undo. What outlives it can be taken back: a session allow, a rule.
 */
import type { ApprovalDecision, ApprovalRequest, Risk } from '@orgus/protocol';
import { isAskKind } from '@orgus/protocol';
import { plural } from '@orgus/ui';
import { isAnswerable } from './askModel.ts';

export type DecisionAction = 'allow-once' | 'allow-session' | 'make-rule' | 'deny';

/** The one name of each action, on every surface. */
export const ACTION_LABEL: Record<DecisionAction, string> = {
  'allow-once': 'Allow once',
  'allow-session': 'Allow for session',
  'make-rule': 'Make it a rule',
  deny: 'Deny',
};

/** The toast after an answer: a noun and a past-tense verb. */
export const ACTION_DONE: Record<DecisionAction, string> = {
  'allow-once': 'Request allowed',
  'allow-session': 'Allowed for this session',
  'make-rule': 'Rule added',
  deny: 'Request denied',
};

/** The keys the Inbox binds to the open request. Hints show only where the key works. */
export const ACTION_KEY: Record<DecisionAction, string> = { 'allow-once': 'a', 'allow-session': 's', 'make-rule': 'r', deny: 'd' };

export interface DecisionInput {
  note?: string;
  pattern?: string;
  pathScope?: 'folder' | 'worktree';
}

/** The body for POST /api/approvals/:id. A rule is scope 'project': the hub writes it and allows the request in one step. */
export function decisionOf(action: DecisionAction, input: DecisionInput = {}): ApprovalDecision {
  const note = input.note?.trim() || undefined;
  switch (action) {
    case 'allow-once':
      return { answer: 'allow', scope: 'once', ...(note ? { note } : {}) };
    case 'allow-session':
      return { answer: 'allow', scope: 'session', pattern: (input.pattern ?? '').trim(), ...(input.pathScope ? { pathScope: input.pathScope } : {}), ...(note ? { note } : {}) };
    case 'make-rule':
      return { answer: 'allow', scope: 'project', pattern: (input.pattern ?? '').trim(), ...(note ? { note } : {}) };
    case 'deny':
      return { answer: 'deny', ...(note ? { note } : {}) };
  }
}

/** Which action a decision is, for labels and busy states. */
export function actionOf(d: Pick<ApprovalDecision, 'answer' | 'scope'>): DecisionAction {
  if (d.answer === 'deny') return 'deny';
  return d.scope === 'session' ? 'allow-session' : d.scope === 'project' ? 'make-rule' : 'allow-once';
}

export const isAllow = (action: DecisionAction): boolean => action !== 'deny';

/** The high-risk rule: allowing a high-risk request takes a confirm that names it, on every surface. */
export const needsConfirm = (a: Pick<ApprovalRequest, 'risk'>, action: DecisionAction): boolean => a.risk === 'high' && isAllow(action);

/** A tool request a person answers with the four actions (not a question, plan or form: those carry an answer). */
export const isToolRequest = (a: Pick<ApprovalRequest, 'kind' | 'ask' | 'state'>): boolean => a.state === 'pending' && !isAnswerable(a);

/**
 * Why an action is not offered on this request, or null. Make it a rule needs a project (the rule
 * is the project's); the hub still checks roles and reach and says why when it refuses.
 */
export function unavailable(a: Pick<ApprovalRequest, 'projectId' | 'tool'>, action: DecisionAction): string | null {
  if (action === 'make-rule' && !a.projectId) return 'This request has no project, so there is nowhere to keep a rule. Allow it for the session instead.';
  if ((action === 'allow-session' || action === 'make-rule') && !a.tool) return 'This request names no tool, so only this one call can be allowed.';
  return null;
}

/** "* matches anything", or what an empty pattern allows. */
export function patternHint(pattern: string, tool: string | undefined): string {
  return pattern.trim() ? '* matches anything.' : `Empty allows any use of ${tool ?? 'this tool'}.`;
}

// ---------------------------------------------------------------------------
// Groups and order
// ---------------------------------------------------------------------------

/** What waits on a person, in one vocabulary on every surface. */
export type AskGroup = 'requests' | 'questions' | 'blocked' | 'assistant' | 'sessions' | 'asked';

export const ASK_GROUP_ORDER: readonly AskGroup[] = ['requests', 'questions', 'blocked', 'assistant', 'sessions', 'asked'];

export const ASK_GROUP_LABEL: Record<AskGroup, string> = {
  requests: 'Requests',
  questions: 'Questions',
  blocked: 'Blocked',
  assistant: 'Assistant',
  sessions: 'Sessions waiting',
  asked: 'Asked',
};

export const RISK_RANK: Record<Risk, number> = { high: 0, medium: 1, low: 2 };

/** What the one order reads: a deadline when there is one, a risk when there is one, and since when it waits. */
export interface Orderable {
  deadline?: number | null;
  risk?: Risk | null;
  at: number;
}

/** The one order inside a group: the soonest deadline first (a deadline outranks risk), then the highest risk, then the longest wait. */
export function compareAsks(x: Orderable, y: Orderable): number {
  const dx = x.deadline ?? Number.POSITIVE_INFINITY;
  const dy = y.deadline ?? Number.POSITIVE_INFINITY;
  if (dx !== dy) return dx < dy ? -1 : 1;
  const rx = x.risk ? RISK_RANK[x.risk] : 3;
  const ry = y.risk ? RISK_RANK[y.risk] : 3;
  return rx - ry || x.at - y.at;
}

/** Pending requests in the one order. */
export function sortRequests<T extends Pick<ApprovalRequest, 'expiresAt' | 'risk' | 'createdAt'>>(list: readonly T[]): T[] {
  return [...list].sort((x, y) => compareAsks({ deadline: x.expiresAt, risk: x.risk, at: x.createdAt }, { deadline: y.expiresAt, risk: y.risk, at: y.createdAt }));
}

/** The group a pending request belongs to. */
export const groupOfRequest = (a: Pick<ApprovalRequest, 'kind' | 'ask'>): AskGroup => (isAnswerable(a) ? 'questions' : 'requests');

// ---------------------------------------------------------------------------
// Bulk
// ---------------------------------------------------------------------------

export type BulkAction = 'allow-once' | 'allow-session' | 'deny';

export const BULK_ACTIONS: readonly BulkAction[] = ['allow-once', 'allow-session', 'deny'];

export interface BulkPlan<T> {
  /** What the action applies to. */
  take: T[];
  /** What it leaves out, and why (shown on the action's hint). */
  skip: { item: T; why: string }[];
  /** High-risk requests among `take` that an allow must confirm by name. */
  high: T[];
}

/**
 * What one bulk action does with a selection: tool requests take allow and deny; a question, plan
 * or form takes only deny (it needs its own answer to be allowed); anything already decided, or
 * that is not a request, is left out. High-risk requests stay in, flagged for the confirm.
 */
export function bulkPlan<T extends Pick<ApprovalRequest, 'kind' | 'ask' | 'state' | 'risk' | 'tool'>>(items: readonly T[], action: BulkAction): BulkPlan<T> {
  const take: T[] = [];
  const skip: BulkPlan<T>['skip'] = [];
  for (const a of items) {
    if (a.state !== 'pending') skip.push({ item: a, why: 'It was answered already.' });
    else if ((isAskKind(a.kind) || isAnswerable(a)) && action !== 'deny') skip.push({ item: a, why: 'A question, plan or form needs its own answer.' });
    else if (action === 'allow-session' && !a.tool) skip.push({ item: a, why: 'It names no tool, so it can only be allowed once.' });
    else take.push(a);
  }
  return { take, skip, high: action === 'deny' ? [] : take.filter((a) => a.risk === 'high') };
}

/** A count and its noun: "1 request", "12 requests". */
export const requestsWord = (n: number): string => plural(n, 'request');
